import React from 'react';
import translate from '@/utils/translate';
import type {CampaignProps, CampaignPaidGigBriefItem} from '@/types/campaign';
import BriefField from '../../shared/BriefField';
import BriefItemHtml from '../../shared/BriefItemHtml';
import BriefSection from '../../shared/BriefSection';
import CampaignHashtagChips from '../../shared/CampaignHashtagChips';
import CaptionGuidelinesBlock from '../../shared/CaptionGuidelinesBlock';
import DeliverableCard from '../../shared/DeliverableCard';
import DividedStack from '../../shared/DividedStack';
import {
  getBriefItemByTitle,
  type SectionsToHide,
} from '../../utils/briefItemFilters';
import styles from './Deliverables.module.scss';

interface DeliverablesProps {
  campaign: CampaignProps;
  briefItems: CampaignPaidGigBriefItem[];
  sectionsToHide: SectionsToHide;
}

function Deliverables({
  campaign,
  briefItems,
  sectionsToHide,
}: DeliverablesProps) {
  const deliverableItems = campaign.campaignBriefDeliverableItems ?? [];
  // When a campaign is cloned from SWAPS (server admin), campaignBriefDeliverableItems
  // are not copied, but the legacy "Deliverables" brief item is. Fall back to its HTML
  // so cloned campaigns still render and remain editable via SWAPS.
  const fallbackDeliverablesItem = getBriefItemByTitle(
    briefItems,
    'Deliverables'
  );

  const productToFeatureItem = getBriefItemByTitle(
    briefItems,
    'Product to feature'
  );
  const contentMandatoriesItem = getBriefItemByTitle(
    briefItems,
    'Content mandatories'
  );
  const postingInstructionsItem = !sectionsToHide.postingInstructions
    ? getBriefItemByTitle(briefItems, 'Posting instructions')
    : undefined;
  const showHashtags =
    !sectionsToHide.campaignHashtag &&
    (campaign.swapMatchKeywords?.length ?? 0) > 0;
  const showCaptions =
    (campaign.campaignBriefCaptionGuidelines?.length ?? 0) > 0;

  if (
    !deliverableItems.length &&
    !fallbackDeliverablesItem?.contentHtml &&
    !productToFeatureItem &&
    !contentMandatoriesItem &&
    !postingInstructionsItem &&
    !showHashtags &&
    !showCaptions
  ) {
    return null;
  }

  const deliverableCards = deliverableItems.length ? (
    <div className={styles.deliverablesList}>
      {deliverableItems.map((item) => (
        <DeliverableCard key={item.id} deliverable={item} />
      ))}
    </div>
  ) : null;

  const deliverablesContentHtml =
    !deliverableCards && fallbackDeliverablesItem?.contentHtml ? (
      <BriefItemHtml contentHtml={fallbackDeliverablesItem.contentHtml} />
    ) : null;

  const productToFeatureRow = productToFeatureItem?.contentHtml ? (
    <BriefField label={translate('Product to feature')}>
      <BriefItemHtml contentHtml={productToFeatureItem.contentHtml} />
    </BriefField>
  ) : null;

  const contentMandatoriesRow = contentMandatoriesItem?.contentHtml ? (
    <BriefField label={translate('Content mandatories')}>
      <BriefItemHtml contentHtml={contentMandatoriesItem.contentHtml} />
    </BriefField>
  ) : null;

  const postingInstructionsRow = postingInstructionsItem?.contentHtml ? (
    <BriefField label={translate('Posting instructions')}>
      <BriefItemHtml contentHtml={postingInstructionsItem.contentHtml} />
    </BriefField>
  ) : null;

  const hashtagsAndCaptionsGroup =
    showHashtags || showCaptions ? (
      <div className={styles.hashtagsAndCaptions}>
        {showHashtags ? (
          <BriefField label={translate('Campaign hashtag')}>
            <CampaignHashtagChips keywords={campaign.swapMatchKeywords ?? []} />
          </BriefField>
        ) : null}
        {showCaptions ? (
          <CaptionGuidelinesBlock
            guidelines={campaign.campaignBriefCaptionGuidelines}
          />
        ) : null}
      </div>
    ) : null;

  return (
    <BriefSection title={translate('Deliverables')}>
      <DividedStack
        items={[
          deliverableCards,
          deliverablesContentHtml,
          productToFeatureRow,
          contentMandatoriesRow,
          postingInstructionsRow,
          hashtagsAndCaptionsGroup,
        ]}
      />
    </BriefSection>
  );
}

export default Deliverables;
